import { Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { WebSocketService } from './websocket.service';
import { PermissionsService } from './permissions.service';
import { OrderService } from './order.service';
import { ReservationService, MyReservationItem } from './reservation.service';

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendiente',
  confirmed: 'Confirmado',
  accepted: 'Aceptado',
  in_progress: 'Preparando tu pedido',
  prepared: 'Tu pedido esta listo',
  completed: 'Pedido completado',
  rejected: 'Pedido rechazado',
  cancelled: 'Pedido cancelado',
};

const STATUS_ICONS: Record<string, string> = {
  pending: '⏳',
  confirmed: '✅',
  accepted: '👍',
  in_progress: '👨‍🍳',
  prepared: '🔔',
  completed: '🎉',
  rejected: '❌',
  cancelled: '🚫',
};

const TERMINAL_STATUSES = ['completed', 'cancelled', 'rejected'];
const RESERVATION_TERMINAL = ['completed', 'cancelled', 'no_show', 'rejected'];

@Injectable({
  providedIn: 'root'
})
export class OrderNotificationService implements OnDestroy {
  private wsSub?: Subscription;
  private ordersSub?: Subscription;
  private reservationsSub?: Subscription;
  private initialized = false;
  private subscribedSessions = new Set<string>();
  private lastNotifiedStatus = new Map<string, string>();
  private reservationTimers: number[] = [];
  private notifiedReservations = new Set<string>();

  constructor(
    private wsService: WebSocketService,
    private permissionsService: PermissionsService,
    private orderService: OrderService,
    private reservationService: ReservationService
  ) {}

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.loadAndSubscribeActiveOrders();
    this.scheduleReservationReminders();

    this.wsSub = this.wsService.orderStatusUpdates$.subscribe(msg => {
      const { session_id, status, previous_status } = msg.data;

      if (this.lastNotifiedStatus.get(session_id) === status) return;
      this.lastNotifiedStatus.set(session_id, status);

      if (TERMINAL_STATUSES.includes(status)) {
        this.unsubscribeOrder(session_id);
      }

      this.showStatusNotification(session_id, status, previous_status);
    });
  }

  subscribeToOrder(sessionId: string, currentStatus: string = 'pending'): void {
    if (!sessionId || this.subscribedSessions.has(sessionId)) return;

    this.wsService.subscribeToOrderStatus(sessionId);
    this.subscribedSessions.add(sessionId);
    this.lastNotifiedStatus.set(sessionId, currentStatus);
  }

  ngOnDestroy(): void {
    this.wsSub?.unsubscribe();
    this.ordersSub?.unsubscribe();
    this.reservationsSub?.unsubscribe();
    this.subscribedSessions.forEach(sid => this.wsService.unsubscribeFromOrderStatus(sid));
    this.subscribedSessions.clear();
    this.reservationTimers.forEach(t => clearTimeout(t));
    this.reservationTimers = [];
    this.initialized = false;
  }

  hasActiveOrders(): boolean {
    return this.subscribedSessions.size > 0;
  }

  private loadAndSubscribeActiveOrders(): void {
    this.ordersSub = this.orderService.getOrderHistory(10).subscribe({
      next: (res) => {
        if (!res.success) return;

        const cutoff = Date.now() - 8 * 60 * 60 * 1000;

        for (const order of res.orders) {
          const sid = order.tpv_order_id || order.session_id;
          const orderTime = order.ordered_at ? new Date(order.ordered_at).getTime() : 0;
          if (sid && !TERMINAL_STATUSES.includes(order.status) && orderTime > cutoff) {
            this.subscribeToOrder(sid, order.status);
          }
        }

        if (this.subscribedSessions.size === 0) {
          console.log('No active orders, disconnecting WebSocket');
          this.wsService.disconnect();
        }
      }
    });
  }

  /**
   * Load upcoming reservations and schedule local push reminders.
   * Reminds 1h before and 15min before the reservation.
   */
  private scheduleReservationReminders(): void {
    this.reservationsSub = this.reservationService.getMyReservations(10).subscribe({
      next: (res) => {
        if (!res.success || !res.reservations) return;

        const now = Date.now();
        for (const r of res.reservations) {
          if (RESERVATION_TERMINAL.includes(r.status)) continue;

          const reservationTime = this.parseReservationTime(r);
          if (!reservationTime || reservationTime < now) continue;

          const diffMs = reservationTime - now;

          // Reminder 1 hour before
          const oneHourBefore = diffMs - 60 * 60 * 1000;
          if (oneHourBefore > 0 && oneHourBefore < 8 * 60 * 60 * 1000) {
            this.scheduleReminder(r, oneHourBefore, '1 hora');
          }

          // Reminder 15 min before
          const fifteenMinBefore = diffMs - 15 * 60 * 1000;
          if (fifteenMinBefore > 0 && fifteenMinBefore < 8 * 60 * 60 * 1000) {
            this.scheduleReminder(r, fifteenMinBefore, '15 minutos');
          }
        }
      }
    });
  }

  private scheduleReminder(reservation: MyReservationItem, delayMs: number, timeLabel: string): void {
    const key = `${reservation.id}-${timeLabel}`;
    if (this.notifiedReservations.has(key)) return;

    const timer = window.setTimeout(() => {
      this.notifiedReservations.add(key);

      const businessName = reservation.business_name || 'el local';
      const title = `📅 Reserva en ${timeLabel}`;
      const body = `Tu reserva en ${businessName} es a las ${reservation.time_start}`;

      this.permissionsService.showNotification(title, {
        body,
        tag: `reservation-reminder-${reservation.id}`,
        requireInteraction: true,
      });
    }, delayMs);

    this.reservationTimers.push(timer);
  }

  private parseReservationTime(r: MyReservationItem): number | null {
    try {
      // reservation_date: "2026-03-17", time_start: "14:00"
      const dateStr = `${r.reservation_date}T${r.time_start}:00`;
      const ts = new Date(dateStr).getTime();
      return isNaN(ts) ? null : ts;
    } catch {
      return null;
    }
  }

  private unsubscribeOrder(sessionId: string): void {
    if (this.subscribedSessions.has(sessionId)) {
      this.wsService.unsubscribeFromOrderStatus(sessionId);
      this.subscribedSessions.delete(sessionId);
    }
  }

  private showStatusNotification(sessionId: string, status: string, previousStatus?: string): void {
    const label = STATUS_LABELS[status] || status;
    const icon = STATUS_ICONS[status] || '📋';
    const title = `${icon} ${label}`;

    let body = `Pedido #${sessionId.substring(0, 8)}`;
    if (status === 'prepared') {
      body += ' - Acercate a recogerlo';
    } else if (status === 'in_progress') {
      body += ' - Lo estan preparando';
    } else if (status === 'completed') {
      body += ' - Gracias por tu compra';
    } else if (status === 'rejected' || status === 'cancelled') {
      body += ' - Contacta con el local si tienes dudas';
    }

    const options: NotificationOptions = {
      body,
      tag: `order-status-${sessionId}`,
      requireInteraction: status === 'prepared',
    };
    (options as any).vibrate = status === 'prepared' ? [200, 100, 200, 100, 200] : [100];

    this.permissionsService.showNotification(title, options);
  }
}
